import React, { useState, useEffect } from 'react';
import { Shield, Award, Users, Clock, ArrowRight, CheckCircle, Star, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../styles/Home.css';

const Inicio = () => {
    const navigate = useNavigate();
    const [currentSlide, setCurrentSlide] = useState(0);

    const heroSlides = [
        {
            title: "Fire Safety",
            subtitle: "Bem-vindo à",
            description: "Oferecemos formação, treinamento e aperfeiçoamento de brigadas de incêndio, além de promover cursos, palestras e serviços para emergências industriais.",
            image: "https://www.kpnsafety.com/wp-content/uploads/2023/10/Mas-alla-extintores-Herramientas-y-tecnicas-modernas-de-proteccion-contra-incendios.jpg"
        },
            
        {
            title: "Proteção Profissional",
            subtitle: "Soluções em Segurança",
            description: "Equipamentos certificados e instalação especializada para proteger vidas e patrimônios.",
            image: "https://elipa.com.br/img/blog_item/proteger-empresa-contra-incendios.jpg"
        },
        {
            title: "Manutenção Confiável",
            subtitle: "Serviços 24/7",
            description: "Manutenção preventiva e suporte contínuo para sistemas de combate a incêndios.",
            image: "https://previews.123rf.com/images/studioworkstock/studioworkstock2012/studioworkstock201200601/160805190-portable-or-industrial-fire-extinguisher-firefighter-equipment-fire-fighting-safety-unit-different.jpg"
        }
    ];

    const stats = [
        { icon: Shield, number: "500+", label: "Clientes Protegidos" },
        { icon: Award, number: "10+", label: "Anos de Experiência" },
        { icon: Users, number: "1500+", label: "Profissionais Certificados" },
        { icon: Clock, number: "24/7", label: "Suporte Disponível" }
    ];

    const testimonials = [
        {
            name: "Marcia Langa",
            company: "Hotel Polana",
            text: "Excelente serviço! A equipe da Fire Safety instalou todo o sistema de detecção no nosso hotel. Profissionais competentes e equipamentos de qualidade.",
            rating: 5
        },
        {
            name: "João Santos",
            company: "Indústria Matola",
            text: "Há 3 anos que trabalhamos com a Fire Safety. Sempre pontuais na manutenção e muito profissionais. Recomendo sem dúvidas.",
            rating: 5
        },
        {
            name: "Ana Costa",
            company: "Shopping Maputo",
            text: "O treinamento oferecido pela equipe foi fundamental para preparar nossos funcionários. Muito didático e prático.",
            rating: 5
        }
    ];

    const [isPaused, setIsPaused] = useState(false);
    useEffect(() => {
        if (isPaused) return;
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [heroSlides.length, isPaused]);

    return (
        <div className="inicio-page">
            {/* Hero Section with Photo Carousel */}
            <section className="hero" aria-label="Carrossel de apresentação" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} onFocusCapture={() => setIsPaused(true)} onBlurCapture={() => setIsPaused(false)}>
                <div className="hero-slider">
                    {heroSlides.map((slide, index) => (
                        <div
                            key={index}
                            className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
                            style={{ backgroundImage: `url(${slide.image})` }}
                            aria-hidden={index !== currentSlide}
                        >
                            <div className="hero-overlay">
                                <div className="hero-content">
                                    <div className="hero-text">
                                        <span className="hero-subtitle">{slide.subtitle}</span>
                                        <h1 className="hero-title">{slide.title}</h1>
                                        <p className="hero-description">{slide.description}</p>
                                        <div className="hero-buttons">
                                            <button
                                                className="btn-secondary-home"
                                                onClick={() => navigate("/contatos")}
                                                aria-label="Contactar agora"
                                            >
                                                <Phone size={20} />
                                                Contactar Agora
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="hero-indicators">
                    {heroSlides.map((_, index) => (
                        <button
                            key={index}
                            type="button"
                            className={`indicator ${index === currentSlide ? 'active' : ''}`}
                            onClick={() => setCurrentSlide(index)}
                            aria-label={`Ir para slide ${index + 1}`}
                            aria-current={index === currentSlide ? 'true' : undefined}
                        />
                    ))}
                </div>
            </section>

            {/* Stats Section */}
            <section className="stats-section" aria-label="Estatísticas da empresa">
                <div className="container">
                    <div className="stats-grid">
                        {stats.map((stat, index) => {
                            const IconComponent = stat.icon;
                            return (
                                <div key={index} className="stat-card">
                                    <div className="stat-icon">
                                        <IconComponent size={32} aria-hidden="true" />
                                    </div>
                                    <div className="stat-content">
                                        <h3>{stat.number}</h3>
                                        <p>{stat.label}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section className="about-section" aria-label="Sobre a empresa">
                <div className="container">
                    <div className="about-grid">
                        <div className="about-content">
                            <span className="section-subtitle">Quem Somos</span>
                            <h2>Especialistas em Segurança Contra Incêndios</h2>
                            <p>
                                Com mais de 10 anos de experiência em Moçambique, somos uma empresa
                                especializada em soluções completas de segurança contra incêndios.
                                Oferecemos equipamentos certificados, instalação profissional e
                                serviços de manutenção para proteger vidas e patrimônios.
                            </p>
                            <div className="about-features">
                                <div className="feature">
                                    <CheckCircle size={20} aria-hidden="true" />
                                    <span>Equipamentos Certificados</span>
                                </div>
                                <div className="feature">
                                    <CheckCircle size={20} aria-hidden="true" />
                                    <span>Instalação Profissional</span>
                                </div>
                                <div className="feature">
                                    <CheckCircle size={20} aria-hidden="true" />
                                    <span>Manutenção Especializada</span>
                                </div>
                                <div className="feature">
                                    <CheckCircle size={20} aria-hidden="true" />
                                    <span>Suporte 24h</span>
                                </div>
                            </div>
                            <button
                                className="btn-outline"
                                onClick={() => navigate('/sobre')}
                                aria-label="Saiba mais sobre nós"
                            >
                                Saiba Mais Sobre Nós
                                <ArrowRight size={20} />
                            </button>
                        </div>
                        <div className="about-image">
                            <img
                                src="https://previews.123rf.com/images/studioworkstock/studioworkstock2012/studioworkstock201200601/160805190-portable-or-industrial-fire-extinguisher-firefighter-equipment-fire-fighting-safety-unit-different.jpg"
                                alt="Equipe Fire Safety em ação"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="testimonials-section" aria-label="Depoimentos de clientes">
                <div className="container">
                    <div className="section-header">
                        <span className="section-subtitle">Depoimentos</span>
                        <h2>O Que Dizem Nossos Clientes</h2>
                    </div>
                    <div className="testimonials-grid">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="testimonial-card">
                                <div className="testimonial-stars">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <Star key={i} size={16} fill="currentColor" aria-hidden="true" />
                                    ))}
                                </div>
                                <p>{testimonial.text}</p>
                                <div className="testimonial-author">
                                    <strong>{testimonial.name}</strong>
                                    <span>{testimonial.company}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section" aria-label="Chamada para ação">
                <div className="container">
                    <div className="cta-content">
                        <div className="cta-text">
                            <h2>Pronto para Proteger Seu Negócio?</h2>
                            <p>
                                Entre em contacto connosco hoje mesmo e solicite um orçamento
                                personalizado para suas necessidades de segurança contra incêndios.
                            </p>
                        </div>
                        <div className="cta-buttons">
                            <button
                                className="btn-primary"
                                onClick={() => navigate('/contatos')}
                                aria-label="Contactar agora"
                            >
                                <Phone size={20} />
                                Contactar Agora
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Inicio;